
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext'; // [NEW]
import { HelmetProvider } from '@dr.pogodin/react-helmet';

import { AppRoutes } from './routes';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { VersionUpdater } from './components/shared/VersionUpdater';
import { NativeUpdateModal } from './components/shared/NativeUpdateModal';
import { initializeCacheManager } from './services/cacheManager';
import { initNativePlugins, initSecureAuth } from './services/native/init';
import { Toaster } from 'react-hot-toast';
import { UnifiedDevTools } from './components/dev/UnifiedDevTools';
import { AnalyticsTracker } from './components/analytics/AnalyticsTracker';
import { ImpersonationBanner } from './components/shared/ImpersonationBanner';
import { AndroidBackButtonHandler } from './components/shared/AndroidBackButtonHandler';
import { DeepLinkHandler } from './components/shared/DeepLinkHandler';
import { CookieProvider } from './contexts/CookieContext';
import { FeatureToggleProvider } from './contexts/FeatureToggleContext';
import { PricingProvider } from './contexts/PricingContext';
import { CookieManager } from './components/privacy/CookieManager';
import { CookieBanner } from './components/privacy/CookieBanner';
import { OfflinePage } from './components/shared/OfflinePage';
import { platform } from './utils/platform';
import './index.css';
import './i18n'; // Initialize i18n

import { logger } from '@deskbay/shared';
import { initSentry, getSentryConfig, SentryErrorBoundary } from './services/sentry';

// --- GLOBAL ZOOM PREVENTION ---
// Execute only in browser environment to prevent SSR/Node crashes
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  // Prevent pinch-to-zoom gestures
  document.addEventListener('gesturestart', function (e) {
    e.preventDefault();
  }, { passive: false });

  document.addEventListener('gesturechange', function (e) {
    e.preventDefault();
  }, { passive: false });

  document.addEventListener('gestureend', function (e) {
    e.preventDefault();
  }, { passive: false });

  // Prevent double-tap to zoom
  let lastTouchEnd = 0;
  document.addEventListener('touchend', function (event) {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });

}
// ------------------------------

/**
 * Async bootstrap — ensures secure storage + Supabase are initialized
 * BEFORE React mounts. This is critical on native platforms where JWT
 * tokens live in Keychain/EncryptedSharedPreferences (async access).
 *
 * Order:
 * 1. initSecureAuth() — migrate tokens, create adapter, init Supabase
 * 2. initializeCacheManager() — non-blocking
 * 3. initNativePlugins() — StatusBar, Keyboard, Haptics, OneSignal
 * 4. Determine initial route (session check)
 * 5. Mount React tree
 */
async function bootstrap() {
  const isNative = Capacitor.isNativePlatform();

  // --- PLATFORM DETECTION ---
  logger.capacitor.info('Platform Detection:', 'index', { platform });

  // Aggiunge classe CSS al root per distinguere nativo da web.
  // Usata da .native-platform .ai-chat-input-area-page in index.css
  // per applicare safe area env() invece del padding fisso per la bottom nav web.
  if (isNative) {
    document.documentElement.classList.add('native-platform');
  }

  // 0. CRITICAL: Initialize Sentry FIRST for error tracking during bootstrap
  initSentry(getSentryConfig());

  // 1. CRITICAL: Initialize secure auth + Supabase BEFORE anything else
  await initSecureAuth();

  // 2. Non-blocking initializations
  initializeCacheManager().catch(e => logger.capacitor.error('Cache initialization error', e, 'index'));
  initNativePlugins().catch(e => logger.capacitor.error('Native plugins initialization error', e, 'index'));

  // 3. Determine initial route for MemoryRouter (native)
  // IMPORTANT: Always start at /dashboard on native. Do NOT call getSession() here.
  // getSession() via SecureStorageAdapter is async and may return null on cold start
  // before the storage plugin reads from Keychain/EncryptedSharedPreferences.
  // That race caused nativeInitialRoute to be set to '/', which flashed
  // RootRedirect → WelcomePage before INITIAL_SESSION could fire in AuthContext.
  //
  // ProtectedRoute already waits for hasInitialized (after INITIAL_SESSION resolves),
  // so starting at /dashboard is safe: unauthenticated users are redirected to /welcome
  // by ProtectedRoute (intro stories → onboarding), all covered by GlobalSplashScreen.
  const nativeInitialRoute = isNative ? '/dashboard' : '/';

  // 4. Router selection
  const Router = isNative ? MemoryRouter : BrowserRouter;
  const routerProps = isNative ? { initialEntries: [nativeInitialRoute] } : {};
  logger.capacitor.info('Router selected:', 'index', { data: isNative ? `MemoryRouter (start: ${nativeInitialRoute})` : 'BrowserRouter' });

  // 5. Mount React tree
  const rootElement = document.getElementById('root');
  if (rootElement) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <HelmetProvider>
          <SentryErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Si è verificato un errore</h1>
            <p>L'errore è stato segnalato al team. Ricarica la pagina per riprovare.</p>
            <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>Ricarica</button>
          </div>}>
          <ErrorBoundary>
            <FeatureToggleProvider>
                <Router {...routerProps}>
                  <ThemeProvider>
                  <AndroidBackButtonHandler />
                  <DeepLinkHandler />
                  <AuthProvider>
                    <PricingProvider>
                      <CookieProvider>
                        {/* [NEW] Notification Provider for Scalable System Notifications */}
                        <NotificationProvider>
                          <OfflinePage />
                          <ImpersonationBanner />
                          <CookieManager />
                          <CookieBanner />
                          <AnalyticsTracker />
                          <AppRoutes />
                          {import.meta.env.DEV && <UnifiedDevTools />}
                        </NotificationProvider>
                      </CookieProvider>
                    </PricingProvider>
                  </AuthProvider>
                  </ThemeProvider>
                </Router>
            </FeatureToggleProvider>
            {/* VersionUpdater isolated from main app tree to prevent i18n blocking */}
            <React.Suspense fallback={null}>
              <VersionUpdater />
              <NativeUpdateModal />
            </React.Suspense>
            <SpeedInsights />
            <Toaster position="bottom-right" />
          </ErrorBoundary>
          </SentryErrorBoundary>
        </HelmetProvider>
      </React.StrictMode>
    );
  }
}

bootstrap().catch(e => {
  console.error('Bootstrap failed:', e);
  // Last resort: try mounting with default config
  import('@deskbay/shared/services/supabase/client').then(({ initSupabase }) => {
    initSupabase();
  });
});
